import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { IForecastForReceiptRepository } from '../interfaces/IForecastForReceiptManage';
import { IError } from '../interfaces/IError';
import {
  DeleteBaseBusinessForecastForReceiptRequest,
  DeleteForecastForReceiptRequest,
  ForecastForReceiptApi,
  GetForecastForReceiptResponseResult,
  GetForecastForReceiptStaffSummaryResponseResult,
  GetForecastForReceiptSummaryResponseResult,
  GetOutputForecastForReceiptResponseResult,
  PostBaseBusinessToForecastForReceiptRequest,
  PostBaseBusinessToForecastForReceiptResponseResult,
  PostForecastForReceiptRequest,
  PostForecastForReceiptResponseResult,
  PutForecastForReceiptRequest,
  BulkUpdateForecastForReceiptRequest,
} from '../axios/generated/forecastForReceiptManage';
import { customAxiosInstance } from '../axios/axiosInstance';
import { axiosErrorHandler } from '../axios/axiosError';

/**
 * 受領用内示を永続化するリポジトリ
 */
export class ForecastForReceiptRepository implements IForecastForReceiptRepository {
  /**
   * 受領用内示を新規作成する
   * @param postForecastForReceiptRequest - 受領用内示作成情報
   */
  async postForecastForReceipt(
    postForecastForReceiptRequest: PostForecastForReceiptRequest
  ): Promise<INetworkResult<PostForecastForReceiptResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const forecastForReceiptApi = new ForecastForReceiptApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await forecastForReceiptApi.postForecastForReceipt(postForecastForReceiptRequest);
      switch (apiResponse.status) {
        case 200:
          const result: PostForecastForReceiptResponseResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 受領用内示の一覧を取得する
   */
  async getForecastForReceipt(): Promise<INetworkResult<GetForecastForReceiptResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const forecastForReceiptApi = new ForecastForReceiptApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await forecastForReceiptApi.getForecastForReceipt();
      switch (apiResponse.status) {
        case 200:
          const result: GetForecastForReceiptResponseResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 受領用内示を編集する
   * @param forecastForReceiptId - 受領用内示ID
   * @param updateForecastForReceiptRequest - 更新情報
   */
  async updateForecastForReceipt(
    forecastForReceiptId: string,
    updateForecastForReceiptRequest: PutForecastForReceiptRequest
  ): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const forecastForReceiptApi = new ForecastForReceiptApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await forecastForReceiptApi.putForecastForReceipt(forecastForReceiptId, updateForecastForReceiptRequest);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 受領用内示を削除する
   * @param deleteForecastForReceiptRequest - 削除情報
   */
  async deleteForecastForReceipt(deleteForecastForReceiptRequest: DeleteForecastForReceiptRequest): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const forecastForReceiptApi = new ForecastForReceiptApi(undefined, undefined, axiosInstance);
    try {
      const apiResponse = await forecastForReceiptApi.deleteForecastForReceipt(deleteForecastForReceiptRequest);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 受領用内示に基幹業務を追加する
   * @param postBaseBusinessToForecastForReceiptRequest - 基幹業務追加情報
   */
  async postBaseBusinessToForecastForReceipt(
    postBaseBusinessToForecastForReceiptRequest: PostBaseBusinessToForecastForReceiptRequest
  ): Promise<INetworkResult<PostBaseBusinessToForecastForReceiptResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const forecastForReceiptApi = new ForecastForReceiptApi(undefined, undefined, axiosInstance);
    try {
      const apiResponse = await forecastForReceiptApi.postBaseBusinessToForecastForReceipt(postBaseBusinessToForecastForReceiptRequest);
      switch (apiResponse.status) {
        case 200:
          const result: PostBaseBusinessToForecastForReceiptResponseResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 受領用内示から基幹業務を削除する
   * @param deleteBaseBusinessForecastForReceiptRequest - 基幹業務削除情報
   */
  async deleteBaseBusinessForecastForReceipt(
    deleteBaseBusinessForecastForReceiptRequest: DeleteBaseBusinessForecastForReceiptRequest
  ): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const forecastForReceiptApi = new ForecastForReceiptApi(undefined, undefined, axiosInstance);
    try {
      const apiResponse = await forecastForReceiptApi.deleteBaseBusinessForecastForReceipt(deleteBaseBusinessForecastForReceiptRequest);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 選択された受領用内示担当者が持つ受領用内示エクセル作成に必要な情報を取得する
   * 取得情報は、受領用内示に紐づく関連取引情報の一覧と受領用内示作成用のテンプレートエクセルバイナリデータ
   * @param forecastForReceiptResponsibleUserId - 受領用内示担当者ID
   */
  async getOutputForecastForReceipt(
    forecastForReceiptResponsibleUserId: string
  ): Promise<INetworkResult<GetOutputForecastForReceiptResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const forecastForReceiptApi = new ForecastForReceiptApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await forecastForReceiptApi.getOutputForecastForReceipt(forecastForReceiptResponsibleUserId);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 担当者ごとの内示回収状況を取得する
   * @param receivedYear - 内示受領年
   * @param receivedMonth - 内示受領月
   */
  async getForecastForReceiptStaffSummary(
    receivedYear: number,
    receivedMonth: number
  ): Promise<INetworkResult<GetForecastForReceiptStaffSummaryResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const forecastForReceiptApi = new ForecastForReceiptApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await forecastForReceiptApi.getForecastForReceiptStaffSummary(receivedYear, receivedMonth);
      switch (apiResponse.status) {
        case 200:
          const result: GetForecastForReceiptStaffSummaryResponseResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 受領用内示ごとの回収状況を取得する（担当者でフィルタ可能）
   * @param receivedYear - 内示受領年
   * @param receivedMonth - 内示受領月
   * @param userIds - フィルタする担当者IDリスト（カンマ区切り）
   */
  async getForecastForReceiptSummary(
    receivedYear: number,
    receivedMonth: number,
    userIds?: string
  ): Promise<INetworkResult<GetForecastForReceiptSummaryResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const forecastForReceiptApi = new ForecastForReceiptApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await forecastForReceiptApi.getForecastForReceiptSummary(receivedYear, receivedMonth, userIds);
      switch (apiResponse.status) {
        case 200:
          const result: GetForecastForReceiptSummaryResponseResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 受領用内示の担当者を一括更新する
   * @param bulkUpdateForecastForReceiptRequest - 一括更新情報
   */
  async bulkUpdateForecastForReceipt(
    bulkUpdateForecastForReceiptRequest: BulkUpdateForecastForReceiptRequest
  ): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const forecastForReceiptApi = new ForecastForReceiptApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await forecastForReceiptApi.bulkUpdateForecastForReceipt(bulkUpdateForecastForReceiptRequest);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
}
